/**
 * DistributionConfig Component
 *
 * Displays the amount input and balance information for airdrop distribution.
 *
 * @component
 */

import React from "react";
import { colors, gradients, commonStyles } from "../styles";

const DistributionConfig = ({
  distributeAmount,
  setDistributeAmount,
  distributeCurrency,
  distributionBalance,
  distributionBalanceLoading,
  hasInsufficientBalance,
  loading,
}) => {
  return (
    <div
      className="form-row distribution-config"
      style={{
        background: colors.white,
        padding: "20px",
        borderRadius: "4px",
        border: `1px solid ${colors.border}`,
        marginBottom: "20px",
        display: "flex",
        gap: "16px",
        flexWrap: "wrap",
        alignItems: "stretch",
      }}
    >
      {/* Amount Input */}
      <div
        className="form-group"
        style={{
          flex: 1,
          minWidth: "180px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <label style={commonStyles.label}>Amount per User</label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            padding: "12px 14px",
            background: "#f0ebfa",
            borderRadius: "4px",
            border: "1px solid #c4b5e0",
          }}
        >
          <input
            type="number"
            step="0.01"
            min="0"
            value={distributeAmount}
            onChange={(e) => setDistributeAmount(e.target.value)}
            placeholder="Enter amount per user"
            disabled={loading}
            style={{
              width: "100%",
              padding: 0,
              border: "none",
              background: "transparent",
              fontSize: "16px",
              fontWeight: 700,
              color: colors.primary,
              outline: "none",
            }}
          />
        </div>
      </div>

      {/* Balance Display */}
      <div
        className="form-group"
        style={{
          flex: 1,
          minWidth: "180px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {distributeCurrency && (
          <>
            <label style={commonStyles.label}>Available Balance</label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                padding: "12px 14px",
                background: hasInsufficientBalance ? "#fff5f5" : "#f0ebfa",
                borderRadius: "4px",
                border: `1px solid ${
                  hasInsufficientBalance ? "#f5c6cb" : "#c4b5e0"
                }`,
              }}
            >
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: hasInsufficientBalance ? "#721c24" : colors.primary,
                }}
              >
                {distributionBalanceLoading
                  ? "Loading..."
                  : distributionBalance !== null
                  ? `${distributionBalance.toFixed(2)} ${distributeCurrency}`
                  : "N/A"}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * DistributionSummary Component
 *
 * Shows the summary of the airdrop distribution before execution.
 */
export const DistributionSummary = ({
  recipientCount,
  distributeAmount,
  distributeCurrency,
  totalDistribution,
  hasInsufficientBalance,
}) => {
  return (
    <div
      style={{
        display: "flex",
        background: colors.white,
        border: `1px solid ${
          hasInsufficientBalance ? "#f5c6cb" : colors.border
        }`,
        borderRadius: "4px",
        margin: "20px 0",
        overflow: "hidden",
      }}
    >
      {/* Recipients */}
      <div
        style={{
          flex: 1,
          padding: "16px 20px",
          borderRight: `1px solid ${colors.border}`,
          background: colors.white,
        }}
      >
        <span
          style={{
            display: "block",
            fontSize: "11px",
            color: colors.gray600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontWeight: 600,
            marginBottom: "4px",
          }}
        >
          Recipients
        </span>
        <span
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: colors.primary,
          }}
        >
          {recipientCount}
        </span>
      </div>

      {/* Amount Each */}
      <div
        style={{
          flex: 1,
          padding: "16px 20px",
          borderRight: `1px solid ${colors.border}`,
          background: colors.white,
        }}
      >
        <span
          style={{
            display: "block",
            fontSize: "11px",
            color: colors.gray600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontWeight: 600,
            marginBottom: "4px",
          }}
        >
          Amount Each
        </span>
        <span
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: colors.primary,
          }}
        >
          {distributeAmount || "0"} {distributeCurrency}
        </span>
      </div>

      {/* Total Distribution */}
      <div
        style={{
          flex: 1,
          padding: "16px 20px",
          background: hasInsufficientBalance
            ? gradients.danger
            : gradients.primary,
        }}
      >
        <span
          style={{
            display: "block",
            fontSize: "11px",
            color: "rgba(255,255,255,0.85)",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontWeight: 600,
            marginBottom: "4px",
          }}
        >
          Total Distribution
        </span>
        <span
          style={{
            fontSize: "22px",
            fontWeight: 700,
            color: colors.white,
          }}
        >
          {totalDistribution.toFixed(2)} {distributeCurrency}
        </span>
      </div>
    </div>
  );
};

export default DistributionConfig;
